import { inject, Injectable } from '@angular/core';
import { KeywordStateService } from './keyword-state.service';
// TODO : Improved typing for better dev experience.
// TODO : Add options to search to only query an entity to improve performance (not searching on all _entities).

interface IndexableEntities {
  [key: string]: any;
  keywordList?: number[];
  uid: number;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalSearchService {
  // KeywordSetService depends on this service to reindex on every association
  // change; reading the keyword dictionary through the shared KeywordStateService
  // (rather than through KeywordSetService directly) avoids a circular DI
  // dependency between the two services.
  private _keywordStateService = inject(KeywordStateService);

  private _entities: { [type: string]: IndexableEntities[] } = {};
  private _searchedFields: { [type: string]: any[] } = {};
  private _SORTING_LIMIT = 500; // limit sorting into results to improve performances.

  // Global indexed values
  public globalIndex: {
    [word: string]: { type: string; uid: string | number }[];
  } = {};

  /**
   * Add entity configuration (values, fields) to search.
   * @param args
   */
  public addEntity<T extends IndexableEntities>(args: {
    type: string;
    values: T[];
    searchedFields: (keyof T)[];
  }) {
    this._entities[args.type] = args.values;
    this._searchedFields[args.type] = args.searchedFields;

    const keywords = this._keywordStateService.$keywords();

    args.values.forEach(item => {
      const normalizedWords = this._normalizeBySearchedFields(item, args.searchedFields);

      const normalizedKeywords =
        item.keywordList?.map(uid => this._normalizeQuery(keywords[uid]?.label || '')) || []; // secure guard if there is no label on keyword

      // Index every values.
      [...normalizedWords, ...normalizedKeywords].forEach(word => {
        if (!this.globalIndex[word]) {
          this.globalIndex[word] = [];
        }

        // prevent creation of empty string.
        if (word) {
          this.globalIndex[word].push({
            type: args.type,
            uid: item.uid,
          });
        }
      });
    });
  }

  /**
   * Search a query. Splits the query into terms so a multi-word query
   * (e.g. "Michel Dupont") matches entities whose indexed words each cover
   * one of the terms (e.g. separate firstName/lastName fields), not just
   * entities indexed under one word containing the whole query verbatim.
   * @param query
   * @returns
   */
  public search(query: string) {
    const normalizedQuery = this._normalizeQuery(query);
    const queryTerms = normalizedQuery.split(/\s+/).filter(term => term.length > 0);
    const results: Record<string, any[]> = {}; // TODO: maybe convert it into a Javascript Map.

    if (!queryTerms.length) {
      return results;
    }

    // Entities matching every query term (matches for term 1, intersected with term 2, ...).
    // Stop early once the intersection is empty: further terms can't add anything back.
    let matches = this._findMatches(queryTerms[0]);
    for (let index = 1; index < queryTerms.length && matches.length; index++) {
      matches = this._intersect(matches, this._findMatches(queryTerms[index]));
    }

    matches.forEach(({ type, uid }) => {
      const matchingEntity = this._entities[type].find(entity => entity.uid === uid);

      if (matchingEntity) {
        if (!results[type]) {
          results[type] = [];
        }
        results[type].push(matchingEntity);
      }
    });

    for (const type in results) {
      if (results[type].length < this._SORTING_LIMIT)
        results[type].sort((a, b) => {
          const fieldValueA = a[this._searchedFields[type][0]];
          const fieldValueB = b[this._searchedFields[type][0]];

          if (!fieldValueA) return 1;
          if (!fieldValueB) return -1;

          return fieldValueA.localeCompare(fieldValueB);
        });
    }

    return results;
  }

  /**
   * Find every entity match (type + uid) indexed under a word containing the given term.
   * A given entity can be indexed under several words matching the same term
   * (e.g. two searched fields both containing it), so duplicates are filtered out.
   * @param term
   * @returns
   */
  private _findMatches(term: string) {
    const matches: { type: string; uid: string | number }[] = [];
    const seenEntityKeys = new Set<string>();

    Object.keys(this.globalIndex)
      .filter(word => word.includes(term))
      .forEach(word =>
        this.globalIndex[word].forEach(match => {
          const entityKey = `${match.type}:${match.uid}`;
          if (seenEntityKeys.has(entityKey)) return;
          seenEntityKeys.add(entityKey);
          matches.push(match);
        })
      );

    return matches;
  }

  /**
   * Intersect two (already deduplicated) match lists, keeping entities present in both.
   * @param matchesA
   * @param matchesB
   * @returns
   */
  private _intersect(
    matchesA: { type: string; uid: string | number }[],
    matchesB: { type: string; uid: string | number }[]
  ) {
    const keysInB = new Set(matchesB.map(match => `${match.type}:${match.uid}`));

    return matchesA.filter(match => keysInB.has(`${match.type}:${match.uid}`));
  }

  /**
   * Find entities of the given types having one searched field strictly equal to the value
   * (case and accent insensitive). Used by the scanner, where a partial match would be misleading.
   * @param types
   * @param value
   * @returns
   */
  public findByExactValue(types: string[], value: string) {
    const normalizedValue = this._normalizeQuery(value);
    const results: Record<string, IndexableEntities[]> = {};

    if (!normalizedValue) {
      return results;
    }

    types.forEach(type => {
      const matchingEntities = (this._entities[type] || []).filter(entity =>
        this._searchedFields[type].some(
          field => this._normalizeQuery(String(entity[field] ?? '')) === normalizedValue
        )
      );

      if (matchingEntities.length) {
        results[type] = matchingEntities;
      }
    });

    return results;
  }

  public clear() {
    this.globalIndex = {};
    this._searchedFields = {};
    this._entities = {};
  }

  /**
   * Normalize an entity item by its searchedFields.
   * @param item
   * @param searchedFields
   * @returns
   */
  private _normalizeBySearchedFields<T>(item: T, searchedFields: (keyof T)[]) {
    return searchedFields
      .map(key =>
        String(item[key] || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
      )
      .filter(word => word.length > 1); // Ignore short words.
  }

  /**
   * Normalize input query.
   * @param query
   * @returns
   */
  private _normalizeQuery(query: string) {
    return query
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }
}
